import { apiClient } from '@/lib/api/client';
import type { ApiEnvelope } from '@/types/api';
import type {
  JuthoorInitiative,
  JuthoorInitiativeStats,
  JuthoorInitiativeStatus,
  JuthoorLand,
  JuthoorParticipantEntry,
  CreateJuthoorInitiativePayload,
} from '@/types/juthoor';

export async function getJuthoorInitiatives(params?: {
  status?: JuthoorInitiativeStatus;
  page?: number;
  per_page?: number;
}): Promise<ApiEnvelope<JuthoorInitiative[]>> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.page) query.set('page', String(params.page));
  if (params?.per_page) query.set('per_page', String(params.per_page));
  const qs = query.toString();
  return apiClient.get<JuthoorInitiative[]>(
    `/api_partner/juthoor/initiatives${qs ? `?${qs}` : ''}`
  );
}

export async function getJuthoorInitiative(
  id: string
): Promise<ApiEnvelope<JuthoorInitiative>> {
  return apiClient.get<JuthoorInitiative>(`/api_partner/juthoor/initiatives/${id}`);
}

export async function createJuthoorInitiative(
  payload: CreateJuthoorInitiativePayload
): Promise<ApiEnvelope<JuthoorInitiative>> {
  return apiClient.post<JuthoorInitiative>('/api_partner/juthoor/initiatives', payload);
}

export async function updateJuthoorInitiative(
  id: string,
  payload: Partial<CreateJuthoorInitiativePayload>
): Promise<ApiEnvelope<JuthoorInitiative>> {
  return apiClient.put<JuthoorInitiative>(
    `/api_partner/juthoor/initiatives/${id}`,
    payload
  );
}

export async function setJuthoorBudget(
  id: string,
  amount: number
): Promise<ApiEnvelope<JuthoorInitiative>> {
  return apiClient.post<JuthoorInitiative>(
    `/api_partner/juthoor/initiatives/${id}/set-budget`,
    { budget: amount }
  );
}

export async function submitJuthoorInitiative(
  id: string
): Promise<ApiEnvelope<{ id: string; status: JuthoorInitiativeStatus }>> {
  return apiClient.post<{ id: string; status: JuthoorInitiativeStatus }>(
    `/api_partner/juthoor/initiatives/${id}/submit`,
    {}
  );
}

export async function getJuthoorInitiativeStats(
  id: string
): Promise<ApiEnvelope<JuthoorInitiativeStats>> {
  return apiClient.get<JuthoorInitiativeStats>(
    `/api_partner/juthoor/initiatives/${id}/stats`
  );
}

export async function getJuthoorLands(
  cityId?: string
): Promise<ApiEnvelope<JuthoorLand[]>> {
  const qs = cityId ? `?city_id=${cityId}` : '';
  return apiClient.get<JuthoorLand[]>(`/api_partner/juthoor/lands${qs}`);
}

export async function getJuthoorParticipants(
  initiativeId: string,
  params?: { page?: number; per_page?: number }
): Promise<ApiEnvelope<JuthoorParticipantEntry[]>> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.per_page) query.set('per_page', String(params.per_page));
  const qs = query.toString();
  return apiClient.get<JuthoorParticipantEntry[]>(
    `/api_partner/juthoor/initiatives/${initiativeId}/purchases${qs ? `?${qs}` : ''}`
  );
}
